// QR payment section — inline panel state, migrated from payment-qr-dialog.tsx
import { useState, useEffect, useRef, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiPost } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { toast } from "@/components/ui/toast";
import { useOrderPaymentSSE } from "./use-order-payment-sse";

const fmtVND = (n: number) => new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";

function fmtCountdown(ms: number): string {
  if (ms <= 0) return "00:00";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
}

interface QRPaymentData {
  qrContent: string;
  content: string;
  amount: number;
  expiresAt: string;
  transactionId: string;
  accountNo: string;
  accountName: string;
  bankBin: string;
}

interface PaymentQRSectionProps {
  orderId: string;
  onSuccess: () => void;
  onBack: () => void;
}

export function PaymentQRSection({ orderId, onSuccess, onBack }: PaymentQRSectionProps) {
  const [qr, setQr] = useState<QRPaymentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const successFiredRef = useRef(false);
  const activeRef = useRef(true);

  const { status: payStatus, refetch } = useOrderPaymentSSE(qr ? orderId : null);

  // Generate QR on mount.
  useEffect(() => {
    activeRef.current = true;
    successFiredRef.current = false;
    setLoading(true);
    setError(null);
    setQr(null);
    apiPost<QRPaymentData>(`/v1/orders/${orderId}/pay/qr`)
      .then((data) => { if (activeRef.current) setQr(data); })
      .catch((err: unknown) => { if (activeRef.current) setError(translateApiError(err)); })
      .finally(() => { if (activeRef.current) setLoading(false); });
    return () => { activeRef.current = false; };
  }, [orderId]);

  // Countdown timer.
  useEffect(() => {
    if (!qr) return;
    const tick = () => setCountdown(Math.max(0, new Date(qr.expiresAt).getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [qr]);

  // SSE: fire success once.
  useEffect(() => {
    if (payStatus === "paid" && !successFiredRef.current) {
      successFiredRef.current = true;
      onSuccess();
    }
  }, [payStatus, onSuccess]);

  // SSE: cancelled by order mutation.
  useEffect(() => {
    if (payStatus === "cancelled") {
      toast.info("QR đã huỷ vì đơn vừa được sửa, vui lòng tạo QR mới");
      onBack();
    }
  }, [payStatus, onBack]);

  const handleCancel = useCallback(async () => {
    setCancelling(true);
    try { await apiPost(`/v1/orders/${orderId}/pay/qr/cancel`); } catch { /* best-effort */ }
    finally { setCancelling(false); onBack(); }
  }, [orderId, onBack]);

  // Esc = cancel + back.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape" && !cancelling) handleCancel(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [cancelling, handleCancel]);

  const expired = countdown === 0 && qr !== null;
  const isPaid = payStatus === "paid";

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={handleCancel} disabled={cancelling || isPaid} className="shrink-0">
          <ArrowLeft size={16} />
        </Button>
        <span className="font-semibold text-base">Thanh toán QR VietQR</span>
      </div>

      {loading && (
        <div className="flex flex-col items-center gap-4 py-10">
          <Loader2 size={40} className="animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Đang tạo mã QR...</p>
        </div>
      )}

      {!loading && error && (
        <div className="space-y-3">
          <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
          <p className="text-xs text-muted-foreground">
            Mẹo: nếu chưa cấu hình QR, có thể dùng nút <strong>“Chuyển khoản (F7)”</strong> để ghi nhận khách đã chuyển khoản mà không cần QR.
          </p>
          <Button variant="outline" size="sm" className="w-full" onClick={handleCancel} disabled={cancelling}>
            Quay lại
          </Button>
        </div>
      )}

      {!loading && qr && (
        <>
          {isPaid && (
            <div className="rounded-md bg-green-50 border border-green-200 p-3 text-center">
              <p className="text-green-700 font-semibold text-base">Thanh toán thành công!</p>
            </div>
          )}
          {expired && !isPaid && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-center">
              <p className="text-amber-700 font-medium text-sm">Mã QR đã hết hạn. Vui lòng tạo lại.</p>
            </div>
          )}

          <div className={`flex justify-center ${expired && !isPaid ? "opacity-30" : ""}`}>
            <div className="rounded-xl border-4 border-primary/20 p-2 bg-white">
              <QRCodeSVG value={qr.qrContent} size={180} />
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Số tiền</span>
              <span className="font-bold text-primary text-base">{fmtVND(qr.amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Nội dung CK</span>
              <span className="font-mono font-medium">{qr.content}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Tài khoản</span>
              <span className="font-medium">{qr.accountNo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Chủ TK</span>
              <span className="font-medium">{qr.accountName}</span>
            </div>
            {!isPaid && (
              <div className="flex justify-between border-t pt-2">
                <span className="text-muted-foreground">Thời gian còn lại</span>
                <span className={`font-mono font-bold ${countdown < 60000 ? "text-destructive" : "text-foreground"}`}>
                  {fmtCountdown(countdown)}
                </span>
              </div>
            )}
          </div>

          {!isPaid && !expired && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className={`inline-block w-2 h-2 rounded-full ${payStatus === "listening" ? "bg-green-400 animate-pulse" : "bg-amber-400"}`} />
              {payStatus === "listening" ? "Đang chờ thanh toán..." : "Kết nối gián đoạn — đang polling"}
            </div>
          )}

          <div className="flex gap-2">
            {!isPaid && (
              <>
                <Button variant="outline" size="sm" className="flex-1" onClick={refetch}>
                  <RefreshCw size={14} className="mr-1" />Kiểm tra lại
                </Button>
                <Button variant="outline" size="sm" className="flex-1 text-destructive hover:text-destructive" onClick={handleCancel} disabled={cancelling}>
                  {cancelling ? "Đang huỷ..." : "Huỷ (Esc)"}
                </Button>
              </>
            )}
            {isPaid && <Button className="w-full" onClick={onBack}>Đóng</Button>}
          </div>
        </>
      )}
    </div>
  );
}
